import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { Pencil, Save, X, Truck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const PRIORITIES = ["Direct", "HOT", "Rush", "Regular", "Sameday", "NextDay"];
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
const field = "h-11 bg-slate-100 border border-slate-300 focus-visible:border-brand-green focus-visible:ring-brand-green/30 font-mono text-sm placeholder:text-slate-400";

const Field = ({ label, children }) => (
  <div className="space-y-1"><Label className="font-mono text-[11px] uppercase tracking-widest text-slate-700">{label}</Label>{children}</div>
);
const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-1 border-b border-dashed border-border">
    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
    <span className="font-mono text-xs text-foreground text-right">{value || "—"}</span>
  </div>
);

export default function TripDetailsDialog({ trip, drivers = [], onClose, onUpdated }) {
  const [d, setD] = useState(trip);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState(null);
  const [driverId, setDriverId] = useState("");

  useEffect(() => {
    setD(trip);
    setEditing(false);
    setDriverId(trip?.driver_id || "");
    if (trip) {
      const pr = trip.pricing || {};
      setF({
        po_number: trip.po_number || "", booking_date: trip.booking_date || "", delivery_date: trip.delivery_date || "",
        service_priority: trip.service_priority || "Regular",
        weight_kg: trip.weight_kg ?? 0, pieces: trip.pieces ?? 1,
        package_description: trip.package_description || "", special_instructions: trip.special_instructions || "",
        pickup_addr: trip.pickup?.address || "", pickup_name: trip.pickup?.contact_name || "", pickup_phone: trip.pickup?.contact_phone || "", pickup_instr: trip.pickup?.instructions || "", pickup_lat: trip.pickup?.lat ?? null, pickup_lng: trip.pickup?.lng ?? null,
        dropoff_addr: trip.dropoff?.address || "", dropoff_name: trip.dropoff?.contact_name || "", dropoff_phone: trip.dropoff?.contact_phone || "", dropoff_instr: trip.dropoff?.instructions || "", dropoff_lat: trip.dropoff?.lat ?? null, dropoff_lng: trip.dropoff?.lng ?? null,
        base_rate: pr.base_rate ?? 25, weight_surcharge: pr.weight_surcharge ?? 0, fuel_surcharge_pct: pr.fuel_surcharge_pct ?? 15,
        waiting_minutes: pr.waiting_minutes ?? 0, waiting_rate: pr.waiting_rate ?? 1, gst_rate: pr.gst_rate ?? 5,
      });
    }
  }, [trip]);

  if (!d || !f) return null;

  const est = () => {
    const wait = Number(f.waiting_minutes) * Number(f.waiting_rate);
    const preSub = Number(f.base_rate) + Number(f.weight_surcharge) + wait;
    const fuel = preSub * (Number(f.fuel_surcharge_pct) / 100);
    const sub = preSub + fuel;
    const gst = sub * (Number(f.gst_rate) / 100);
    return { sub: sub.toFixed(2), gst: gst.toFixed(2), total: (sub + gst).toFixed(2), fuel: fuel.toFixed(2) };
  };
  const e = est();
  const locked = ["delivered", "cancelled"].includes(d.status);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        po_number: f.po_number || null, booking_date: f.booking_date, delivery_date: f.delivery_date,
        service_priority: f.service_priority,
        pickup: { address: f.pickup_addr, contact_name: f.pickup_name, contact_phone: f.pickup_phone, instructions: f.pickup_instr, lat: f.pickup_lat, lng: f.pickup_lng },
        dropoff: { address: f.dropoff_addr, contact_name: f.dropoff_name, contact_phone: f.dropoff_phone, instructions: f.dropoff_instr, lat: f.dropoff_lat, lng: f.dropoff_lng },
        weight_kg: Number(f.weight_kg), pieces: Number(f.pieces),
        package_description: f.package_description, special_instructions: f.special_instructions,
        pricing: {
          base_rate: Number(f.base_rate), weight_surcharge: Number(f.weight_surcharge), fuel_surcharge_pct: Number(f.fuel_surcharge_pct),
          waiting_minutes: Number(f.waiting_minutes), waiting_rate: Number(f.waiting_rate), gst_rate: Number(f.gst_rate),
        },
      };
      const { data } = await api.put(`/deliveries/${d.id}`, payload);
      setD(data.delivery); setEditing(false); toast.success("Booking updated"); onUpdated?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const reassign = async () => {
    if (!driverId || driverId === d.driver_id) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/deliveries/${d.id}/assign`, { driver_id: driverId });
      setD(data.delivery); toast.success(`Reassigned to ${data.delivery.driver_number || ""} ${data.delivery.driver_name}`.trim()); onUpdated?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!trip} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto bg-card border-2" data-testid="trip-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tighter text-2xl flex items-center justify-between gap-2 pr-6">
            <span>{d.delivery_number}</span>
            <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-slate-300 text-muted-foreground">{d.status}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Driver / reassign */}
        <div className="border border-slate-300 bg-slate-50 p-3 space-y-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-brand-green flex items-center gap-2"><Truck className="w-3 h-3" /> Driver Assignment</div>
          <div className="font-mono text-xs text-foreground">Current: <span className="font-bold">{d.driver_name ? `${d.driver_number || ""} ${d.driver_name}` : "Unassigned"}</span></div>
          {!locked && (
            <div className="flex gap-2">
              <Select value={driverId} onValueChange={setDriverId}>
                <SelectTrigger className={field} data-testid="reassign-driver-select"><SelectValue placeholder="Select driver" /></SelectTrigger>
                <SelectContent>
                  {drivers.map((dr) => <SelectItem key={dr.id} value={dr.id}>{dr.driver_number || "—"} {dr.name} · {dr.online ? "ONLINE" : "OFFLINE"}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={reassign} disabled={busy || !driverId || driverId === d.driver_id} className="h-11 bg-brand-green text-black hover:bg-brand-green/90 font-mono uppercase text-xs font-bold shrink-0" data-testid="reassign-btn">
                {d.driver_id ? "Reassign" : "Assign"}
              </Button>
            </div>
          )}
        </div>

        {!editing ? (
          <div className="space-y-3">
            <div className="space-y-0.5">
              <Row label="Account" value={`${d.account_code} · ${d.account_name}`} />
              {d.walk_in_customer?.name && <Row label="Walk-in" value={`${d.walk_in_customer.name}${d.walk_in_customer.phone ? " · " + d.walk_in_customer.phone : ""}`} />}
              <Row label="Priority" value={d.service_priority} />
              <Row label="PO #" value={d.po_number} />
              <Row label="Booking Date" value={d.booking_date} />
              <Row label="Delivery Date" value={d.delivery_date} />
              <Row label="Weight / Pieces" value={`${d.weight_kg}kg · ${d.pieces}pc`} />
              <Row label="Package" value={d.package_description} />
            </div>
            <div className="border border-slate-300 bg-slate-50 p-3 space-y-1">
              <div className="font-mono text-[10px] uppercase tracking-widest text-brand-success">↑ Pickup</div>
              <div className="font-mono text-xs">{d.pickup?.address}</div>
              <div className="font-mono text-[10px] text-muted-foreground">{[d.pickup?.contact_name, d.pickup?.contact_phone].filter(Boolean).join(" · ")}</div>
              {d.pickup?.instructions && <div className="font-mono text-[10px] text-muted-foreground">ℹ {d.pickup.instructions}</div>}
            </div>
            <div className="border border-slate-300 bg-slate-50 p-3 space-y-1">
              <div className="font-mono text-[10px] uppercase tracking-widest text-brand-orange">↓ Delivery</div>
              <div className="font-mono text-xs">{d.dropoff?.address}</div>
              <div className="font-mono text-[10px] text-muted-foreground">{[d.dropoff?.contact_name, d.dropoff?.contact_phone].filter(Boolean).join(" · ")}</div>
              {d.dropoff?.instructions && <div className="font-mono text-[10px] text-muted-foreground">ℹ {d.dropoff.instructions}</div>}
            </div>
            {d.special_instructions && (
              <div className="border border-brand-danger/50 bg-brand-danger/5 p-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-brand-danger flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Special Instructions</div>
                <div className="font-mono text-xs mt-1">{d.special_instructions}</div>
              </div>
            )}
            <div className="border-2 border-brand-green/50 bg-brand-green/5 p-3 font-mono text-xs grid grid-cols-4 gap-2">
              <div><div className="text-[10px] uppercase text-muted-foreground">Fuel</div><div>${d.pricing?.fuel_surcharge?.toFixed(2)}</div></div>
              <div><div className="text-[10px] uppercase text-muted-foreground">Subtotal</div><div>${d.pricing?.subtotal?.toFixed(2)}</div></div>
              <div><div className="text-[10px] uppercase text-muted-foreground">GST</div><div>${d.pricing?.gst?.toFixed(2)}</div></div>
              <div><div className="text-[10px] uppercase text-brand-green">Total</div><div className="text-brand-green font-bold">${d.pricing?.total?.toFixed(2)}</div></div>
            </div>
            {(d.history?.length > 0) && (
              <div className="border border-slate-300 bg-slate-50 p-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Activity Log</div>
                <div className="space-y-2 max-h-40 overflow-y-auto" data-testid="trip-history">
                  {[...d.history].reverse().map((h, i) => (
                    <div key={i} className="flex gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-brand-green mt-1.5 shrink-0" />
                      <div className="min-w-0">
                        <div className="font-mono text-[11px] text-foreground break-words">{h.note}</div>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{fmtTime(h.at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!locked && (
              <Button onClick={() => setEditing(true)} variant="outline" className="w-full h-11 font-mono uppercase text-xs border-slate-300" data-testid="edit-trip-btn"><Pencil className="w-4 h-4 mr-2" />Edit Booking</Button>
            )}
            {locked && <div className="font-mono text-[10px] uppercase text-muted-foreground text-center">This booking is {d.status} and cannot be edited.</div>}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Service Priority">
                <Select value={f.service_priority} onValueChange={(v) => setF({ ...f, service_priority: v })}>
                  <SelectTrigger className={field} data-testid="edit-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="PO #"><Input value={f.po_number} onChange={(ev) => setF({ ...f, po_number: ev.target.value })} className={field} data-testid="edit-po-input" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Booking Date"><Input type="date" value={f.booking_date} onChange={(ev) => setF({ ...f, booking_date: ev.target.value })} className={field} /></Field>
              <Field label="Delivery Date"><Input type="date" value={f.delivery_date} onChange={(ev) => setF({ ...f, delivery_date: ev.target.value })} className={field} /></Field>
            </div>
            <div className="border border-slate-300 bg-slate-50 p-3 space-y-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-brand-success">↑ Pickup</div>
              <AddressAutocomplete value={f.pickup_addr} onChange={(addr, ex) => setF({ ...f, pickup_addr: addr, pickup_lat: ex.lat ?? null, pickup_lng: ex.lng ?? null })} placeholder="Address (BC)" testId="edit-pickup-address" />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Contact Name" value={f.pickup_name} onChange={(ev) => setF({ ...f, pickup_name: ev.target.value })} className={field} />
                <Input placeholder="Contact Phone" value={f.pickup_phone} onChange={(ev) => setF({ ...f, pickup_phone: ev.target.value })} className={field} />
              </div>
              <Input placeholder="Pickup instructions" value={f.pickup_instr} onChange={(ev) => setF({ ...f, pickup_instr: ev.target.value })} className={field} />
            </div>
            <div className="border border-slate-300 bg-slate-50 p-3 space-y-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-brand-orange">↓ Delivery</div>
              <AddressAutocomplete value={f.dropoff_addr} onChange={(addr, ex) => setF({ ...f, dropoff_addr: addr, dropoff_lat: ex.lat ?? null, dropoff_lng: ex.lng ?? null })} placeholder="Address (BC)" testId="edit-dropoff-address" />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Contact Name" value={f.dropoff_name} onChange={(ev) => setF({ ...f, dropoff_name: ev.target.value })} className={field} />
                <Input placeholder="Contact Phone" value={f.dropoff_phone} onChange={(ev) => setF({ ...f, dropoff_phone: ev.target.value })} className={field} />
              </div>
              <Input placeholder="Delivery instructions" value={f.dropoff_instr} onChange={(ev) => setF({ ...f, dropoff_instr: ev.target.value })} className={field} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Weight (kg)"><Input type="number" step="0.1" min="0" value={f.weight_kg} onChange={(ev) => setF({ ...f, weight_kg: ev.target.value })} className={field} /></Field>
              <Field label="Pieces"><Input type="number" min="1" value={f.pieces} onChange={(ev) => setF({ ...f, pieces: ev.target.value })} className={field} /></Field>
              <Field label="Package Desc"><Input value={f.package_description} onChange={(ev) => setF({ ...f, package_description: ev.target.value })} className={field} /></Field>
            </div>
            <Field label="Special Instructions"><Textarea value={f.special_instructions} onChange={(ev) => setF({ ...f, special_instructions: ev.target.value })} className="bg-slate-100 border border-slate-300 font-mono text-sm" data-testid="edit-special-instr" /></Field>
            <div className="border-2 border-brand-green/50 bg-brand-green/5 p-3 space-y-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-brand-green">Pricing Breakdown</div>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Base Rate ($)"><Input type="number" step="0.01" value={f.base_rate} onChange={(ev) => setF({ ...f, base_rate: ev.target.value })} className={field} /></Field>
                <Field label="Weight Surch ($)"><Input type="number" step="0.01" value={f.weight_surcharge} onChange={(ev) => setF({ ...f, weight_surcharge: ev.target.value })} className={field} /></Field>
                <Field label="Fuel %"><Input type="number" step="0.1" value={f.fuel_surcharge_pct} onChange={(ev) => setF({ ...f, fuel_surcharge_pct: ev.target.value })} className={field} /></Field>
                <Field label="Wait Min"><Input type="number" step="1" value={f.waiting_minutes} onChange={(ev) => setF({ ...f, waiting_minutes: ev.target.value })} className={field} /></Field>
                <Field label="Wait $/min"><Input type="number" step="0.01" value={f.waiting_rate} onChange={(ev) => setF({ ...f, waiting_rate: ev.target.value })} className={field} /></Field>
                <Field label="GST %"><Input type="number" step="0.1" value={f.gst_rate} onChange={(ev) => setF({ ...f, gst_rate: ev.target.value })} className={field} /></Field>
              </div>
              <div className="font-mono text-xs grid grid-cols-4 gap-2 pt-2 border-t border-dashed border-border">
                <div><div className="text-[10px] uppercase text-muted-foreground">Fuel</div><div>${e.fuel}</div></div>
                <div><div className="text-[10px] uppercase text-muted-foreground">Subtotal</div><div>${e.sub}</div></div>
                <div><div className="text-[10px] uppercase text-muted-foreground">GST</div><div>${e.gst}</div></div>
                <div><div className="text-[10px] uppercase text-brand-green">Total</div><div className="text-brand-green font-bold" data-testid="edit-preview-total">${e.total}</div></div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setEditing(false)} variant="outline" disabled={busy} className="flex-1 h-11 font-mono uppercase text-xs border-slate-300" data-testid="edit-cancel-btn"><X className="w-4 h-4 mr-2" />Cancel</Button>
              <Button onClick={save} disabled={busy} className="flex-1 h-11 bg-brand-green text-black hover:bg-brand-green/90 font-mono uppercase text-xs font-bold" data-testid="edit-save-btn"><Save className="w-4 h-4 mr-2" />{busy ? "Saving..." : "Save Changes"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
